<template>
  <div
    v-if="assignmentCompletion"
    class="d-flex flex-column ga-4"
  >
    <AssignmentHeader
      @close="$emit('close')"
      :assignment="assignment"
    />

    <v-card
      v-if="isScorm"
      border
    >
      <StandardScormPlayer
        @exit="handleScormExit"
        :completed="scormCompleted"
        :completion-subtitle="scormCompletionSubtitle"
        :completion-title="scormCompletionTitle"
        :error="scormError"
        :launch-link="scormLaunchLink"
        :loading="scormLoading"
      />
    </v-card>

    <v-card
      v-else-if="asset"
      border
    >
      <ContentPlayer
        @complete="$emit('video-progress', 100)"
        @update-video-progress="$emit('video-progress', $event)"
        :url="asset"
        :watched-video-progress="assignmentCompletion.video_progress"
      />
    </v-card>

    <template v-if="course.restricted">
      <AssignmentHomework
        @change="$emit('reload')"
        :assignment="assignment"
        :assignment-completion="assignmentCompletion"
      />

      <CommentsCard
        @change="$emit('reload')"
        :assignment="assignment"
        :model-id="assignment.id"
        model-type="assignment"
      />
    </template>
  </div>

  <div v-else>
    <v-progress-linear indeterminate />
  </div>
</template>

<script setup>
import AssignmentHeader from '@/manager/components/assignments/AssignmentHeader.vue';
import AssignmentHomework from '@/manager/components/assignments/AssignmentHomework.vue';
import CommentsCard from '@/shared/components/learning/CommentsCard.vue';
import ContentPlayer from '@/shared/components/learning/ContentPlayer.vue';
import useApi from '@/shared/composables/useApi';

const { api } = useApi();

const props = defineProps({
  assignment: {
    type: Object,
    default: null,
  },
  assignmentCompletion: {
    type: Object,
    default: null,
  },
  course: {
    type: Object,
    default: null,
  },
  isLastAssignment: {
    type: Boolean,
    default: false,
  },
  tab: {
    type: String,
    default: null,
  },
});

const emit = defineEmits(['close', 'reload', 'video-progress']);

const isScorm = computed(() => {
  return props.assignment?.lesson_type === 'scorm';
});

const asset = computed(() => {
  return props.assignment?.assets?.en;
});

const scormCompletionTitle = computed(() => "You're finished!");
const scormCompletionSubtitle = computed(() =>
  props.isLastAssignment ? 'Click Finish to continue.' : 'Click Next to continue.',
);

const scormLaunchLink = ref(null);
const scormLoading = ref(false);
const scormError = ref(null);
const scormCompleted = ref(false);

async function fetchScormLaunchLink() {
  if (!props.assignmentCompletion?.id) return;

  scormLoading.value = true;
  scormError.value = null;
  scormCompleted.value = false;
  scormLaunchLink.value = null;

  try {
    const response = await api.assignmentCompletion.scormLaunch(props.assignmentCompletion.id);
    scormLaunchLink.value = response.data.launch_link;
  } catch (err) {
    scormError.value = err.response?.data?.error || 'Failed to load SCORM content';
  } finally {
    scormLoading.value = false;
  }
}

async function handleScormExit() {
  scormCompleted.value = true;
  try {
    await api.assignmentCompletion.scormSync(props.assignmentCompletion.id);
  } finally {
    emit('reload');
  }
}

watch(
  () => props.assignmentCompletion?.id,
  (id) => {
    if (id && isScorm.value) {
      void fetchScormLaunchLink();
    }
  },
  { immediate: true },
);
</script>
